<template>
  <div class="collect-seedpoints-form__invoice">
    <div class="row mb-3 hidden-m-down">
      <div class="col-3">
        <h4 class="h4 bold" v-text="translate('lblCropType')"></h4>
      </div>
      <div class="col-3">
        <h4 class="h4 bold" v-text="translate('lblVariety')"></h4>
      </div>
      <div class="col-3">
        <h4 class="h4 bold" v-text="translate('lblQuantity')"></h4>
      </div>
    </div>
    <div v-for="(cultureGroup, cIndex) in groups" :key="cIndex">
      <div class="row"
           v-for="(invoicePosition, vIndex) in cultureGroup.positions" :key="`${cIndex}-${vIndex}`"
      >
        <div :class="['col-12 col-l-3', vIndex === 0 ? 'mb-3' : '']">
          <image-dropdown v-if="vIndex === 0"
                          :label="translate('lblChooseCropType')"
                          :options="culturesWithVariants"
                          :initial-value="cultureGroup.culture"
                          @change="changeCropTypeOfGroup($event, cultureGroup)"
          ></image-dropdown>
        </div>

        <div class="col-12 col-m-4 col-l-3 mb-3">
          <image-dropdown
            :label="translate('lblChooseVariety')"
            :options="cultureGroup.culture.varieties"
            :initial-value="invoicePosition.variety"
            :has-error="showErrors && invoicePosition.errors.variety"
            @change="changeVarietyOfPosition($event, invoicePosition, cultureGroup)"
          ></image-dropdown>
        </div>

        <div class="col-12 col-m-4 col-l-3 mb-3">
          <div class="floating-input-label">
            <input type="text"
                   :class="['floating-input-label__input', { 'user-error': showErrors && invoicePosition.errors.quantity }]"
                   placeholder=" "
                   required maxlength="10"
                   :id="`quantity-${cIndex}-${vIndex}`"
                   v-model="invoicePosition.quantity"
                   pattern="\d+"
                   @change="changeQuantity(invoicePosition, cultureGroup)"
            >
            <label class="floating-input-label__label" :for="`quantity-${cIndex}-${vIndex}`" v-text="translate('lblQuantity')"></label>
          </div>
        </div>
        <div class="col-12 col-m-4 col-l-3 mb-3" v-if="vIndex < cultureGroup.positions.length - 1"></div>
        <div class="col-12 col-m-4 col-l-3 mb-3" v-else>
          <button class="button button--medium button--primary mb-2"
                  type="button"
                  @click="addVariety(cultureGroup)"
          >
            <svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg"><path d="M11.968 9.97V.87H9.97v9.1H.87v1.998h9.1v9.1h1.998v-9.1h9.1V9.97h-9.1z" /></svg>
            <span v-if="translate('btnAddVariety')" v-text="translate('btnAddVariety')"></span>
          </button>
        </div>
      </div>

    </div>
    <div class="row">
      <div class="col-12">
        <button class="button button--medium button--primary button--add-crop"
                type="button"
                @click="addCrop"
        >
          <svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg"><path d="M11.968 9.97V.87H9.97v9.1H.87v1.998h9.1v9.1h1.998v-9.1h9.1V9.97h-9.1z" /></svg>
          <span v-text="translate('btnAddCropType')"></span>
        </button>
      </div>
    </div>

    <loading-indicator v-if="loading"></loading-indicator>
  </div>
</template>

<script>
/* eslint-disable no-param-reassign */
import ImageDropdown from './ImageDropdown.vue';
import LoadingMixin from '../../shared/mixins/LoadingMixin';

export default {
  name: 'Invoice',
  data: () => ({
    culturesWithVariants: [],
  }),
  props: {
    apiUrl: String,
    tenant: String,
    language: String,
    cultures: Array,
    groups: {
      type: Array,
      default: () => ([
        {
          culture: {
            varieties: null,
          },
          positions: [
            {
              variety: null,
              quantity: null,
              errors: {
                variety: true,
                quantity: true,
              },
            },
          ],
        },
        {
          culture: {
            varieties: null,
          },
          positions: [
            {
              variety: null,
              quantity: null,
              errors: {
                variety: false,
                quantity: false,
              },
            },
          ],
        },
        {
          culture: {
            varieties: null,
          },
          positions: [
            {
              variety: null,
              quantity: null,
              errors: {
                variety: false,
                quantity: false,
              },
            },
          ],
        },
      ]),
    },
    showErrors: Boolean,
  },
  methods: {
    getEmptyPosition() {
      return {
        variety: null,
        quantity: null,
        errors: {
          variety: false,
          quantity: false,
        },
      };
    },
    getPositionErrorState(position, cultureGroup) {
      const groupHasAnyValidPositions = cultureGroup.positions.some((pos) => pos.variety && pos.quantity);
      return {
        variety: !position.variety && (!groupHasAnyValidPositions || !!position.quantity),
        quantity: !position.quantity && (!groupHasAnyValidPositions || !!position.variety),
      };
    },
    addVariety(group) {
      group.positions.push(this.getEmptyPosition());
      this.$forceUpdate();
    },
    addCrop() {
      // eslint-disable-next-line
      this.groups.push({ culture: { varieties: null }, positions: [this.getEmptyPosition()] });
      this.$forceUpdate();
    },
    getVarietyOptions(cultureGroup) {
      if (cultureGroup.key) {
        const culture = this.cultures.find((option) => option.key === cultureGroup.key);
        if (culture && culture.varieties) {
          return culture.varieties;
        }
      }
      return null;
    },
    async getCultureVarieties() {
      const query = [];
      query.push('query ($tenant: String!, $language: String!) {');
      query.push('products(tenant: $tenant, language: $language) {'
        + ' crop '
        + ' variety '
        + ' varietyId '
        + ' images: celumImageUrlMap '
        + ' attributeMap(names:["type"]) '
        + '}');
      query.push('}');

      this.startRequest();
      await fetch(`${this.apiUrl}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
        body: JSON.stringify({
          query: query.join(' '),
          variables: {
            tenant: this.tenant,
            language: this.language,
          },
        }),
      })
        .then((response) => response.json())
        .then((response) => {
          response.data.products.forEach((product) => {
            let type;

            if (product.attributeMap?.type) {
              if (product.attributeMap.type.shortcut) {
                type = product.attributeMap.type.shortcut;
              }
              if (product.attributeMap?.type?.shortcuts) {
                type = product.attributeMap?.type?.shortcuts[0];
              }
            }

            const variety = {
              key: product.varietyId,
              name: product.variety,
              image: product.images?.mainImage?.length ? product.images?.mainImage[0]['image-125'] : null,
              crop: {
                id: product.crop,
                type,
              },
            };

            const foundCultures = this.cultures.filter((culture) => culture.id === variety.crop.id);
            let foundCulture;

            if (foundCultures.length > 0) {
              if (variety.crop.type) {
                // use culture with correct type
                foundCulture = foundCultures.find((culture) => culture.type && culture.type.includes(variety.crop.type));
              }
              if (!foundCulture) {
                // either type is not provided or type not found → use main culture (without types)
                foundCulture = foundCultures.find((culture) => !culture.type || !culture.type.length);
              }
            }
            if (!foundCulture) {
              // no (main) culture available
              console.warn('No culture found for variety crop', variety.crop);
              return;
            }

            // Add variety to corresponding culture
            if (!foundCulture.varieties) foundCulture.varieties = [];
            foundCulture.varieties.push(variety);
          });

          this.cultures.forEach((culture) => {
            if (culture.varieties) {
              culture.varieties = culture.varieties.sort(
                (a, b) => a.name.localeCompare(b.name, this.language),
              );
            }
          });
        });
      this.finishRequest();

      this.culturesWithVariants = this.cultures.filter((c) => c.varieties);

      this.groups.forEach((cultureGroup) => {
        if (cultureGroup.culture?.key) {
          const culture = this.cultures.find((option) => option.key === cultureGroup.culture.key);
          if (culture && culture.varieties) {
            cultureGroup.culture = culture;
          }
        }
      });
      this.$forceUpdate();
    },
    changeCropTypeOfGroup(selectedCulture, group) {
      group.culture = selectedCulture || { varieties: null };
      const position = this.getEmptyPosition();
      group.positions = [position];
      position.errors = this.getPositionErrorState(position, group);
      this.$nextTick(() => {
        this.$emit('change', this.groups);
        this.$forceUpdate();
      });
    },
    changeVarietyOfPosition(selectedVariety, position, cultureGroup) {
      position.variety = selectedVariety;
      position.errors = this.getPositionErrorState(position, cultureGroup);

      this.$emit('change', this.groups);
      this.$forceUpdate();
    },
    changeQuantity(position, cultureGroup) {
      position.quantity = Number.parseInt(position.quantity, 10);
      if (Number.isNaN(position.quantity) || position.quantity < 1) {
        position.quantity = '';
      }
      position.errors = this.getPositionErrorState(position, cultureGroup);

      this.$emit('change', this.groups);
      this.$forceUpdate();
    },
  },
  created() {
    this.getCultureVarieties();
  },
  mixins: [LoadingMixin],
  components: {
    ImageDropdown,
  },
};
</script>

<style lang="scss">
.form-component .collect-seedpoints-form__invoice {
  .button {
    @media (max-width: 63.9375em) {
      width: 100%;
    }
    @media (min-width: 20em) {
      height: rem(42px);
    }

    &.button--add-crop {
      min-width: calc(.25 * (100% + 24px) - 24px);

      @media (min-width: 80em) {
        min-width: calc(.25 * (100% + 30px) - 30px);
      }
    }

    svg {
      fill: currentColor;
      width: rem(16px);

      + span {
        margin-left: 1rem;
      }
    }
  }
}
</style>
